import { useEffect } from "react";

/**
 * Desktop only; renders nothing. The Live Sync engine runs in Electron's main
 * process and holds no credentials of its own — this bridge hands it the
 * session (token + plan) whenever the app has one, and takes it away on
 * sign-out, which stops every watch.
 *
 * Runs at App level rather than on the Live Sync page so that sync resumes on
 * app launch without the user visiting any particular screen — the window can
 * even be closed again straight away and uploading carries on in the tray.
 */
export default function DesktopSyncBridge() {
	useEffect(() => {
		if (!window.photentra?.liveSync) return undefined;

		let lastSent = null;

		const push = async () => {
			const token = localStorage.getItem("token");
			const userId = localStorage.getItem("userId");

			if (!token) {
				if (lastSent !== null) {
					window.photentra.liveSync.setSession({ token: null });
					lastSent = null;
				}
				return;
			}
			if (token === lastSent) return;
			lastSent = token;

			// The plan decides compression profile and RAW entitlement. Best-effort:
			// the engine defaults to the standard profile until this arrives.
			let subscriptionType;
			try {
				const API_BASE_URL = (await import("../config")).default;
				const res = await fetch(`${API_BASE_URL}/api/user/${userId}`, {
					headers: { Authorization: `Bearer ${token}` },
				});
				if (res.ok) subscriptionType = (await res.json()).subscription_type;
			} catch {
				/* offline is fine — sync retries anyway */
			}

			window.photentra.liveSync.setSession({ token, subscriptionType });
		};

		push();

		// localStorage has no same-tab change event, so poll cheaply. Sign-in,
		// sign-out, and token refresh are all caught within a few seconds.
		const interval = setInterval(push, 5000);
		return () => clearInterval(interval);
	}, []);

	return null;
}
